import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { inject, isDevMode, NgModule } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideChatBot } from '@coin/chat-bot-feature';
import { AuthUserState } from '@coin/shared/auth/data-access';
import { AuthComponentsModule, AuthGmsAngularClientModule, UserState } from '@coin/shared/auth/feature';
import { AuthGmsClientConfig } from '@coin/shared/auth/util';
import { GroupSearchModule } from '@coin/shared/group-search/feature';
import { ApiModule, Configuration } from '@coin/shared/core/data-access';
import { ApiModule as ApiModuleV2, Configuration as ConfigurationV2 } from '@coin/shared/core/data-access-v2';
import { FeatureFlagDirective } from '@coin/shared/core/feature-directives';
import { LegacyComponentsModule } from '@coin/shared/core/feature-legacy-components';
import {
  IconButtonComponent,
  UpdateNotifierComponent,
  V2ButtonComponent,
  V2CardComponent
} from '@coin/shared/core/ui-storybook';
import { PreventRightClickDirective } from '@coin/shared/core/util-directives';
import { environment } from '@coin/shared/core/util-environments';
import { provideDefaultDateFormats } from '@coin/shared/core/util-helpers';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import { ApiModule as CosmosApiModule, Configuration as CosmosConfiguration } from './api/generated';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { httpInterceptorProviders } from './http-interceptors';
import { SharedModule } from './shared/shared.module';
import { SocRoutingCompatibilityModule } from './soc-routing-compatibility/soc-routing-compatibility.module';
import { HelpMenuComponent } from './static/header/components/help-menu/help-menu.component';
import { UserMenuComponent } from './static/header/components/user-menu/user-menu.component';
import { HeaderComponent } from './static/header/header.component';
import { CollapsedBarComponent } from './static/sidebar/components/collapsed-bar/collapsed-bar.component';
import { SidebarMenuItemComponent } from './static/sidebar/components/sidebar-menu-item/sidebar-menu-item.component';
import { SidebarComponent } from './static/sidebar/sidebar.component';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    UserMenuComponent,
    HelpMenuComponent,
    SidebarComponent,
    CollapsedBarComponent,
    SidebarMenuItemComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    SocRoutingCompatibilityModule,
    AppRoutingModule,
    ToastrModule.forRoot(),
    AuthGmsAngularClientModule.forRoot({
      ...environment.authentication,
      ...environment.cosmosAuthentication
    } as AuthGmsClientConfig),
    TranslateModule.forRoot({
      loader: provideTranslateHttpLoader({
        prefix: `${environment.cmsContentProdBucketCloudfront}translations/cosmos/`,
        suffix: '.json'
      })
    }),
    SharedModule,
    MatIconModule,
    MatDialogModule,
    BrowserAnimationsModule,
    LegacyComponentsModule,
    ApiModule.forRoot(() => new Configuration({ basePath: environment.api.baseUrl.replace('/cosmosapi', '') })),
    ApiModuleV2.forRoot(() => new ConfigurationV2({ basePath: environment.api.baseUrl.replace('/cosmosapi', '') })),
    CosmosApiModule.forRoot(() => new CosmosConfiguration({ basePath: environment.api.baseUrl })),
    MatBadgeModule,
    GroupSearchModule,
    V2CardComponent,
    PreventRightClickDirective,
    FeatureFlagDirective,
    AuthComponentsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWithDelay:5000'
    }),
    UpdateNotifierComponent,
    V2ButtonComponent,
    IconButtonComponent
  ],
  providers: [
    { provide: AuthUserState, useExisting: UserState },
    httpInterceptorProviders,
    TranslateService,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideDefaultDateFormats(),
    provideChatBot(),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } } // fix for https://jira.siemens-energy.com/browse/GMTEC-22822
  ]
})
export class AppModule {
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);

  constructor() {
    this.loadIcons();
  }

  private loadIcons(): void {
    this.matIconRegistry.addSvgIcon(
      'help_icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../assets/svgs/help.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'cylinder',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../assets/svgs/org-management/cylinder.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'bee',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../assets/svgs/org-management/bee.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'co_purple',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../assets/svgs/co-purple.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'ms_teams',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../assets/svgs/ms-teams.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'org',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../assets/svgs/org.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'share',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../assets/svgs/share.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'sort',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../assets/svgs/legal-entities/sort.svg')
    );
  }
}
